import { createExperimentNormalizer, createShapeNormalizer } from '@change-corgi/fcm/normalizers';

import { defineFcm } from '../shared/factory';

const elementShape = {
	type: 'object',
	optional: true,
	shape: {
		value: { type: 'string' },
	},
} as const;

// i18n plural forms (CLDR categories); the form for the count is picked by the locale's plural rules,
// falling back to `other`
const pluralElementShape = {
	type: 'object',
	optional: true,
	shape: {
		zero: { type: 'string', optional: true },
		one: { type: 'string', optional: true },
		two: { type: 'string', optional: true },
		few: { type: 'string', optional: true },
		many: { type: 'string', optional: true },
		other: { type: 'string' },
	},
} as const;

export const FCM_PSF_COMBO_COPY_EXPERIMENT = defineFcm('psf_combo_copy_experiment')
	.asJson(
		createShapeNormalizer({
			type: 'object',
			shape: {
				experimentName: { type: 'string' },
				exclusion_group_experiment_id: { type: 'string', optional: true },
				variants: {
					type: 'other',
					normalizer: createExperimentNormalizer(
						['control', 'fake_control', 'variant_1', 'variant_2'],
						createShapeNormalizer({
							type: 'object',
							shape: {
								subheadingText2: elementShape,
								subheadingText3: elementShape,
								promoteButtonWithGoalText: elementShape,
								skipLinkText: elementShape,
								contributionHeroPlural: pluralElementShape,
							},
						}),
					),
				},
			},
		}),
	)
	.withDefault(undefined)
	.withMerger(({ globalValue, countryValue, localeValue }) => {
		return countryValue ?? localeValue ?? globalValue;
	})
	.toConfig({
		owner: 'squad-koi',
		scope: 'corgi-front-end',
		context: 'psf combo (promote-or-share) page',
		description: `Config-driven copy experiment for the combo screen: per-variation copy overrides, set per locale.

			- experimentName: experiment service name for the locale this value is set on
			- exclusion_group_experiment_id: if set, the session must also be picked for this id by
			  promote_or_share_combo_exclusion_group (keeps the test exclusive with other combo experiments)
			- variants: per-variation copy overrides for these elements:
			  subheadingText2 (the ask), subheadingText3 (the body), promoteButtonWithGoalText (the promote CTA),
			  skipLinkText (the skip link)
			- contributionHeroPlural: replaces the "N heroes have already contributed" line, with plural forms:
			  { one?, other, zero?, two?, few?, many? } — set the forms the locale uses (e.g. ja-JP only needs other);
			  %{promoter_count} is the localized promoter count; may contain HTML; not shown when there are no promoters
			- each element: { value: string } — value replaces the translated string, unset element keeps the default i18n copy
			- fake_control is always rendered with the control payload by the front-end (A/A) — no need to set it
			- leave control empty ({}) to render today's i18n copy
			- subheadingText3 value may contain HTML (rendered via the page's sanitized Html component:
			  p, br, strong, em, b, i, ul, ol, li, a)`,
		mergeLogic: 'Displays in order of country, locale, then global',
		template: {
			experimentName: '__invalid_experiment__',
			variants: {
				control: {},
				variant_1: {
					subheadingText2: { value: 'New ask with %{chip_in_amount_formatted}' },
					subheadingText3: { value: '<p>Intro</p><ul><li>First point</li><li>Second point</li></ul>' },
					promoteButtonWithGoalText: { value: 'New CTA with %{chip_in_amount_formatted}' },
					skipLinkText: { value: 'New skip link' },
					contributionHeroPlural: {
						one: '<strong>%{promoter_count} hero</strong> has already contributed:',
						other: '<strong>%{promoter_count} heroes</strong> have already contributed:',
					},
				},
			},
		},
	});
